<template>
    <div v-if="isOrderLive"
         data-component="RedeemOrderButton"
         class="redeem-order-button">
        <button v-if="hasTicketsToRedeem"
                data-testid="redeemOrderButton"
                type="button"
                class="btn btn-xl btn--redeem-order"
                :class="[buttonColorClass]"
                :disabled="isDisabled"
                @click.prevent="redeemOrder">
            {{ l('ORDER_DETAILS.REDEEM_ORDER') }}
            <small v-if="hasSessionNotToday"
                   data-testid="redeemOrderSessionWarning">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ l('ORDER_DETAILS.SOME_TICKETS_NOT_FOR_TODAY') }}
            </small>
        </button>
        <div v-else
             class="redeem-order-button__success-indicator-wrapper"
             data-testid="allTicketsRedeemed">
            <span class="indicator indicator-success">
                {{ l('ORDER_DETAILS.ALL_TICKETS_REDEEMED') }}
            </span>
        </div>
    </div>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import { container } from '@/container';
import {
    isCurrentGroupForSessionTodayCheck,
    redemptionEnabledForOrderLineCheck,
} from '@/epos/vue/pages/order-details/functions/redemptionFunctions';

export default {
    name: 'RedeemOrderButton',

    props: {
        // The groups belonging to the current tab (Tickets tab only - this button only ever
        // performs a "redeem", never an "unredeem", so it doesn't need direction logic).
        groups: {
            type: Array,
            required: true,
        },
        isOrderLive: {
            type: Boolean,
            required: true,
        },
    },

    computed: {
        /**
         * Groups within this tab that still have something left to redeem.
         *
         * Mirrors the ticket-branch of RedeemGroupButton's direction check, but only the
         * "is there anything left to redeem" half of it - this button never unredeems.
         *
         * @return {OrderItemGroup[]}
         */
        redeemableGroups() {
            return this.groups.filter((group) => this.groupHasTicketsToRedeem(group));
        },

        /**
         * @return {boolean}
         */
        hasTicketsToRedeem() {
            return this.redeemableGroups.length > 0;
        },

        /**
         * @return {string}
         */
        buttonText() {
            return this.hasTicketsToRedeem
                ? this.l('ORDER_DETAILS.REDEEM_ORDER')
                : this.l('ORDER_DETAILS.ALL_TICKETS_REDEEMED');
        },

        /**
         * Disable the button if any line across the tab can't currently be redeemed (e.g.
         * incomplete waivers) or already has a redemption in progress / errored.
         *
         * @return {boolean}
         */
        isDisabled() {
            const allItems = this.groups.flatMap((group) => group.items);

            if (allItems.some((orderLine) => !redemptionEnabledForOrderLineCheck(orderLine))) {
                return true;
            }

            return allItems.some((orderLine) => {
                return orderLine.redemptionIsInProgress()
                    || orderLine.redemptionError
                    || !!orderLine.iteminstances?.find((itemInstance) => {
                        return itemInstance.redemptionIsInProgress() || itemInstance.redemptionError;
                    });
            });
        },

        /**
         * Whether some of what this button would redeem is for a session on another day.
         *
         * @return {boolean}
         */
        hasSessionNotToday() {
            return this.hasTicketsToRedeem
                && this.redeemableGroups.some((group) => isCurrentGroupForSessionTodayCheck(group) === false);
        },

        /**
         * The colour class for the button, mirroring RedeemGroupButton's
         * currentGroupRedeemAllButtonColorClass but aggregated across every redeemable group
         * on the tab rather than a single group.
         *
         * - green/success = every redeemable group is for a session today (or has no session).
         * - orange/warning = at least one redeemable group's session isn't today.
         *
         * There's no red/danger case here (unlike RedeemGroupButton) because this button only
         * ever redeems, rather than flipping into an "unredeem" state.
         *
         * @see RedeemGroupButton.currentGroupRedeemAllButtonColorClass for the single-group version.
         *
         * @return {string}
         */
        buttonColorClass() {
            return this.hasSessionNotToday ? 'btn-warning' : 'btn-success';
        },
    },

    methods: {
        /**
         * @param {OrderItemGroup} group
         *
         * @return {boolean}
         */
        groupHasTicketsToRedeem(group) {
            return group.items.some((orderLine) => {
                if (orderLine.isExpired()) {
                    return false;
                }

                if (orderLine.hasIndividualTicketInstances()) {
                    return orderLine.summaryItemInstanceNetRedemptions() < orderLine.qty;
                }

                return orderLine.redeemed < orderLine.qty;
            });
        },

        redeemOrder() {
            container.eventBus.broadcast(
                EventNames.REDEEM_ORDER_BUTTON_REDEEM_ORDER,
                this.redeemableGroups.map((group) => group.ID)
            );
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

.redeem-order-button {
    text-align: center;

    .redeem-order-button__success-indicator-wrapper {
        height: @btn-xl-height;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

@media (max-width: @mobile-max) {
    // The card stretches the button to the full width of the screen on mobile, so the wrapper needs to pass that on.
    .btn--redeem-order {
        width: 100%;
    }
}
</style>
